import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, PhoneIncoming, Users, X } from "lucide-react";
import api from "@/lib/crmApi";
import { cn } from "@/lib/utils";

const POLL_MS = 8000;

function FlashCard({ icon: Icon, tone, title, subtitle, onClick, onClose }) {
  const tones = {
    indigo: "border-indigo-300 bg-indigo-50 text-indigo-700",
    emerald: "border-emerald-300 bg-emerald-50 text-emerald-700",
    sky: "border-sky-300 bg-sky-50 text-sky-700",
  };
  return (
    <div
      onClick={() => { onClick?.(); onClose?.(); }}
      className={cn(
        "animate-alert-flash cursor-pointer w-80 rounded-xl border bg-white shadow-xl p-3 flex items-center gap-3",
        "hover:shadow-2xl transition-shadow"
      )}
      data-testid="realtime-alert"
    >
      <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0 border", tones[tone])}>
        <Icon size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-slate-900 truncate">{title}</div>
        <div className="text-xs text-slate-500 truncate">{subtitle}</div>
      </div>
      {/* Les toasts personnalisés n'héritent pas du bouton de <Toaster closeButton> :
          on le pose ici, sans déclencher la navigation de la carte. */}
      <button
        type="button"
        data-testid="realtime-alert-close"
        aria-label="Fermer la notification"
        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
        className="shrink-0 -mr-1 p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
      >
        <X size={15} />
      </button>
    </div>
  );
}

export default function RealtimeAlerts({ onPing }) {
  const navigate = useNavigate();
  const sinceRef = useRef(new Date().toISOString());
  const seen = useRef(new Set());

  useEffect(() => {
    let stopped = false;

    const flash = (opts) => {
      onPing?.();
      toast.custom((id) => <FlashCard {...opts} onClose={() => toast.dismiss(id)} />, { duration: 6000 });
    };

    const poll = async () => {
      if (document.hidden) return;
      try {
        const { data } = await api.get(`/events/stream?since=${encodeURIComponent(sinceRef.current)}`);
        sinceRef.current = data.now;

        const newProspects = (data.prospects || []).filter((p) => !seen.current.has(`c:${p.id}`));
        newProspects.forEach((p) => seen.current.add(`c:${p.id}`));
        if (newProspects.length > 3 || (data.prospects_total || 0) > newProspects.length) {
          const n = data.prospects_total;
          flash({
            icon: Users, tone: "indigo",
            title: `${n} nouveaux prospects`,
            subtitle: "Cliquez pour voir la liste des entreprises",
            onClick: () => navigate("/entreprises"),
          });
        } else {
          newProspects.forEach((p) => flash({
            icon: UserPlus, tone: "indigo",
            title: `Nouveau prospect : ${p.nom}`,
            subtitle: [p.ville, p.status].filter(Boolean).join(" · ") || "Entreprise ajoutée",
            onClick: () => navigate(`/entreprises/${p.id}`),
          }));
        }

        const newCalls = (data.calls || []).filter((c) => !seen.current.has(`k:${c.id}`));
        newCalls.forEach((c) => seen.current.add(`k:${c.id}`));
        newCalls.forEach((c) => flash({
          icon: PhoneIncoming, tone: c.caller_type === "CLIENT" ? "emerald" : "sky",
          title: `Appel entrant : ${c.company_name || c.phone_number}`,
          subtitle: `${c.caller_type === "CLIENT" ? "Client" : c.caller_type === "PROSPECT" ? "Prospect" : "Nouveau"} · ${c.phone_number}`,
          onClick: () => navigate("/telephonie"),
        }));
      } catch {
        /* réseau ponctuel — on réessaiera au prochain tick */
      }
    };

    const timer = setInterval(() => { if (!stopped) poll(); }, POLL_MS);
    return () => { stopped = true; clearInterval(timer); };
  }, [navigate, onPing]);

  return null;
}
